import { type ReactNode, Suspense, lazy } from 'react'
import { Routes, Route } from 'react-router-dom'
const DashboardPage = lazy(() => import('./pages/Dashboard'))
const LoginPage = lazy(() => import('./pages/Login'))
const SignUpPage = lazy(() => import('./pages/SignUp'))
const EmailConfirmPage = lazy(() => import('./pages/EmailConfirm'))
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPassword'))
const ResetPasswordPage = lazy(() => import('./pages/ResetPassword'))
const GoogleCallback = lazy(() => import('./pages/GoogleCallback'))
const ExtensionAuth = lazy(() => import('./pages/ExtensionAuth'))
const CreateOrganizationPage = lazy(() => import('./pages/onboarding/CreateOrganization'))
import ProtectedRoute from './state/ProtectedRoute'
import NotFoundPage from './pages/NotFound'
const ProfilePage = lazy(() => import('./pages/Profile'))
const StoreConfigPage = lazy(() => import('./pages/StoreConfig'))
const BillingPage = lazy(() => import('./pages/Billing'))
const InviteAcceptPage = lazy(() => import('./pages/InviteAccept'))
const KingOfMarketplacesPage = lazy(() => import('./pages/KingOfMarketplaces'))
const ProductsPage = lazy(() => import('./pages/Products'))
const ProductAddPage = lazy(() => import('./pages/ProductAdd'))
const ProductDetailPage = lazy(() => import('./pages/ProductDetail'))
const ProductVisibilityPage = lazy(() => import('./pages/ProductVisibility'))
const CompetitorsPage = lazy(() => import('./pages/Competitors'))
const CompetitorAddPage = lazy(() => import('./pages/CompetitorAdd'))
const CompetitorDetailPage = lazy(() => import('./pages/CompetitorDetail'))
const NotificationsPage = lazy(() => import('./pages/Notifications'))
const NotificationReportsPage = lazy(() => import('./pages/NotificationReports'))
const NotificationReportPage = lazy(() => import('./pages/NotificationReport'))
const HelpPage = lazy(() => import('./pages/Help'))
const CopilotPage = lazy(() => import('./pages/Copilot'))
const CategoriesPage = lazy(() => import('./pages/Categories'))
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { OrgSync } from '@/features/organization/components/OrgSync'
import { RequireOrgAdmin } from '@/features/organization/components/RequireOrgAdmin'
import { AuthRedirect } from '@/features/auth/components/AuthRedirect'
import { RequireCopilot } from '@/features/copilot/components/RequireCopilot'
import { RequireCategoryTree } from '@/features/categories/components/RequireCategoryTree'

export default function App() {
  function AuthLayout({ children }: { children: ReactNode }) {
    return <>{children}</>
  }

  return (
    <div className="min-h-screen bg-[var(--mph-bg-app)] text-[var(--mph-text-primary)] font-sans antialiased">
      <Suspense fallback={<div>Loading...</div>}>
        <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            <AuthLayout>
              <LoginPage />
            </AuthLayout>
          }
        />
        <Route
          path="/signup"
          element={
            <AuthLayout>
              <SignUpPage />
            </AuthLayout>
          }
        />
        <Route
          path="/verify-email/:token"
          element={
            <AuthLayout>
              <EmailConfirmPage />
            </AuthLayout>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <AuthLayout>
              <ForgotPasswordPage />
            </AuthLayout>
          }
        />
        <Route
          path="/reset-password/:token"
          element={
            <AuthLayout>
              <ResetPasswordPage />
            </AuthLayout>
          }
        />
        <Route
          path="/auth/callback/google"
          element={
            <AuthLayout>
              <GoogleCallback />
            </AuthLayout>
          }
        />
        <Route
          path="/extension-auth"
          element={
            <AuthLayout>
              <ExtensionAuth />
            </AuthLayout>
          }
        />

        {/* Onboarding routes */}
        <Route
          path="/onboarding/create-organization"
          element={
            <ProtectedRoute>
              <CreateOrganizationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/invite/:token"
          element={
            <ProtectedRoute>
              <InviteAcceptPage />
            </ProtectedRoute>
          }
        />

        {/* Multi-tenancy routes: /o/:orgId */}
        <Route
          path="/o/:orgId"
          element={
            <ProtectedRoute>
              <OrgSync />
            </ProtectedRoute>
          }
        >
          <Route element={<DashboardLayout />}>
            <Route index element={<NotFoundPage />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="billing" element={<RequireOrgAdmin><BillingPage /></RequireOrgAdmin>} />
            <Route path="stores/config" element={<RequireOrgAdmin><StoreConfigPage /></RequireOrgAdmin>} />
            <Route path="products" element={<ProductsPage />} />
            <Route path="products/add" element={<ProductAddPage />} />
            <Route path="products/:source/:productId" element={<ProductDetailPage />} />
            <Route path="product-visibility" element={<ProductVisibilityPage />} />
            <Route path="competitors" element={<CompetitorsPage />} />
            <Route path="competitors/add" element={<CompetitorAddPage />} />
            <Route path="competitors/:source/:productId" element={<CompetitorDetailPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="notifications/reports" element={<NotificationReportsPage />} />
            <Route path="notifications/reports/:reportId" element={<NotificationReportPage />} />
            <Route path="help" element={<HelpPage />} />
            <Route path="copilot" element={<RequireCopilot><CopilotPage /></RequireCopilot>} />
            <Route path="copilot/:conversationId" element={<RequireCopilot><CopilotPage /></RequireCopilot>} />
            <Route path="categories" element={<RequireCategoryTree><CategoriesPage /></RequireCategoryTree>} />
            <Route
              path="king-of-marketplaces"
              element={
                <ProtectedRoute requireKmpAdmin>
                  <KingOfMarketplacesPage />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Route>

        {/* Root route -> AuthRedirect handles organization routing */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AuthRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          path="*"
          element={
            <ProtectedRoute>
              <NotFoundPage />
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
    </div>
  )
}
